import * as React from 'react';
import { Paper, Tab, Tabs } from '@material-ui/core';
import StorefrontIcon from '@material-ui/icons/Storefront';
import { AppTabs } from '../../components';
import {
  OrderLogView,
  MenuMappingsView,
  BoltOrdersPreviewView,
  DailySalesView,
  ExternalSettingsView,
} from './components';
import { Shops } from '../../enums';

interface Props {
  // When set, the in-page shop selector is hidden and this shop is used
  // for every sub-tab. The per-shop nav wrappers (PekingExternal,
  // StreetFoodExternal) pass this so each left-menu entry is locked to
  // one outlet. When undefined, the legacy in-page toggle is rendered.
  forcedShopId?: number;
}

export enum ExternalOrdersTabList {
  BOLT_PREVIEW = 'bolt-preview',
  DAILY_SALES = 'daily-sales',
  ORDER_LOG = 'order-log',
  MENU_MAPPINGS = 'menu-mappings',
  SETTINGS = 'settings',
}

// Legacy in-page shop selector. Kept for the bare /external-orders
// route (no forcedShopId). The dedicated /external-orders/peking and
// /external-orders/street-food routes bypass this entirely.
const SHOP_OPTIONS = [
  { id: Shops.TO_GO, label: 'Peking To Go' },
  { id: Shops.STREET_FOOD, label: 'Peking Street Food' },
];

export const ExternalOrders: React.FC<Props> = ({ forcedShopId }) => {
  const [shopId, setShopId] = React.useState<number>(
    forcedShopId ?? Shops.TO_GO,
  );

  // If the route changes (Peking ↔ SF), sync the active shop so child
  // components refetch against the new outlet.
  React.useEffect(() => {
    if (forcedShopId !== undefined && forcedShopId !== shopId) {
      setShopId(forcedShopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedShopId]);

  const tabs = [
    { label: 'Live Orders', value: ExternalOrdersTabList.BOLT_PREVIEW },
    { label: 'Daily Sales', value: ExternalOrdersTabList.DAILY_SALES },
    { label: 'Raw Order Log', value: ExternalOrdersTabList.ORDER_LOG },
    { label: 'Menu Mappings', value: ExternalOrdersTabList.MENU_MAPPINGS },
    { label: 'Settings', value: ExternalOrdersTabList.SETTINGS },
  ];

  return (
    <React.Fragment>
      {forcedShopId === undefined && (
        <Paper square style={{ marginBottom: 16 }}>
          <Tabs
            value={shopId}
            onChange={(_, value) => setShopId(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            {SHOP_OPTIONS.map((s) => (
              <Tab
                key={s.id}
                value={s.id}
                icon={<StorefrontIcon />}
                label={s.label}
              />
            ))}
          </Tabs>
        </Paper>
      )}
      <AppTabs tabs={tabs}>
        {(activeTab) => {
          switch (activeTab) {
            case ExternalOrdersTabList.BOLT_PREVIEW:
              return <BoltOrdersPreviewView shopId={shopId} />;
            case ExternalOrdersTabList.DAILY_SALES:
              return <DailySalesView shopId={shopId} />;
            case ExternalOrdersTabList.ORDER_LOG:
              return <OrderLogView shopId={shopId} />;
            case ExternalOrdersTabList.MENU_MAPPINGS:
              return <MenuMappingsView shopId={shopId} />;
            case ExternalOrdersTabList.SETTINGS:
              return <ExternalSettingsView shopId={shopId} />;
            default:
          }
        }}
      </AppTabs>
    </React.Fragment>
  );
};

// Thin per-shop wrappers used by the dedicated left-menu nav entries.
// Each one locks the page to a single outlet and hides the in-page
// shop selector entirely.
export const PekingExternal: React.FC = () => (
  <ExternalOrders forcedShopId={Shops.TO_GO} />
);
export const StreetFoodExternal: React.FC = () => (
  <ExternalOrders forcedShopId={Shops.STREET_FOOD} />
);
